import { compressImage } from "./imageCompress";

export const API_URL = process.env.REACT_APP_API_URL || "https://api.oltodo.com/api";
export const SOCKET_URL = API_URL.replace(/\/api\/?$/, "");

const getHeaders = (isFormData?: boolean): Record<string, string> => ({
  ...(isFormData ? {} : { "Content-Type": "application/json" }),
  Authorization: `Bearer ${localStorage.getItem("oltodo_token") || ""}`,
});

// Rafraîchit l'access token (expire au bout de 15 min côté serveur) via le refresh token.
// Mutualisé entre appels concurrents pour éviter que deux requêtes en 401 en même temps
// ne consomment chacune le refresh token (il est à usage unique, avec rotation).
let refreshPromise: Promise<string | null> | null = null;

// Prévient AppContext qu'une session est morte (refresh token invalide/expiré) — sans ça, le
// localStorage était bien nettoyé mais l'état React (token/user en mémoire) restait périmé :
// l'appli continuait d'afficher l'écran connecté avec chaque requête échouant en silence (401
// jamais visible nulle part), plutôt que de renvoyer proprement vers la connexion. Même principe
// que setVerificationGateListener juste en dessous, et que oltodo-admin/src/utils/api.ts.
let onSessionExpired: (() => void) | null = null;
export const setOnSessionExpired = (fn: (() => void) | null) => { onSessionExpired = fn; };

// Pour identifier "cette session-ci" dans Réglages > Sécurité (voir GET /users/me/sessions?current=
// et POST /users/me/sessions/revoke-others) — jamais dérivable de l'access token côté serveur, le
// client est seul à savoir quel refresh token est le sien.
export const getStoredRefreshToken = () => localStorage.getItem("oltodo_refresh_token");

const refreshAccessToken = (): Promise<string | null> => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    const rt = localStorage.getItem("oltodo_refresh_token");
    if (!rt) return null;
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: rt }),
      });
      if (!res.ok) throw new Error("refresh failed");
      const data = await res.json();
      if (data.accessToken) localStorage.setItem("oltodo_token", data.accessToken);
      if (data.refreshToken) localStorage.setItem("oltodo_refresh_token", data.refreshToken);
      return data.accessToken || null;
    } catch {
      localStorage.removeItem("oltodo_token");
      localStorage.removeItem("oltodo_refresh_token");
      localStorage.removeItem("oltodo_user");
      onSessionExpired?.();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

// Requête authentifiée générique : si le token a expiré (401), on le rafraîchit
// automatiquement puis on rejoue la requête une seule fois avant d'abandonner.
export const authFetch = async (path: string, options: RequestInit = {}) => {
  const isFormData = options.body instanceof FormData;
  let res = await fetch(`${API_URL}${path}`, { ...options, headers: { ...getHeaders(isFormData), ...(options.headers || {}) } });
  if (res.status === 401) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      res = await fetch(`${API_URL}${path}`, { ...options, headers: { ...getHeaders(isFormData), ...(options.headers || {}) } });
    }
  }
  return res;
};

// Un intermédiaire (nginx devant l'API, un WAF, un proxy) peut répondre par une page d'erreur
// HTML (413 "payload too large", 502/504, etc.) plutôt que laisser passer la requête jusqu'à
// Express — `r.json()` plantait alors avec "JSON Parse error: Unexpected character: <", un
// message incompréhensible pour l'utilisateur qui masquait la vraie cause (ex: vidéo trop
// volumineuse). On lit toujours le texte brut d'abord, JSON.parse ensuite : en cas d'échec, on
// retombe sur un message générique exploitable plutôt que de laisser l'exception de parsing
// remonter telle quelle jusqu'à l'écran.
// Compte non vérifié (voir middleware/auth.ts#isExemptFromVerificationGate) : jusqu'ici chaque
// écran retombait sur son propre message d'erreur générique, qui masquait complètement la vraie
// cause. Un composant monté une seule fois à la racine (VerificationGateModal, voir App.tsx)
// s'abonne ici pour afficher un message clair dès qu'une requête écrite est bloquée.
let verificationGateListener: (() => void) | null = null;
export const setVerificationGateListener = (fn: (() => void) | null) => { verificationGateListener = fn; };

const parseJsonSafe = async (res: Response) => {
  const text = await res.text();
  try {
    const data = text ? JSON.parse(text) : {};
    if (res.status === 403 && data?.code === "account_not_verified") verificationGateListener?.();
    return data;
  } catch {
    return { error: res.ok ? "Réponse invalide du serveur" : `Erreur serveur (${res.status})` };
  }
};

// Upload d'une image (avatar/couverture/...) — POST /uploads/:type, retourne l'URL publique.
export const uploadImage = async (file: File, type: string): Promise<string> => {
  // Toujours compressée en WebP avant envoi — jamais le fichier brut choisi par l'utilisateur,
  // quels que soient son format ou son poids d'origine (voir utils/imageCompress.ts, qui
  // retombe sur le fichier original si le navigateur ne sait pas le décoder).
  const compressed = await compressImage(file);
  const formData = new FormData();
  formData.append("file", compressed);
  const res = await authFetch(`/uploads/${type}`, { method: "POST", body: formData });
  const data = await parseJsonSafe(res);
  return data.url;
};

export const api = {
  get: (path: string) => authFetch(path).then(parseJsonSafe),
  post: (path: string, body: any) =>
    authFetch(path, { method: "POST", body: body instanceof FormData ? body : JSON.stringify(body) }).then(parseJsonSafe),
  put: (path: string, body: any) =>
    authFetch(path, { method: "PUT", body: body instanceof FormData ? body : JSON.stringify(body) }).then(parseJsonSafe),
  patch: (path: string, body: any) =>
    authFetch(path, { method: "PATCH", body: body instanceof FormData ? body : JSON.stringify(body) }).then(parseJsonSafe),
  delete: (path: string, body?: any) =>
    authFetch(path, { method: "DELETE", body: body !== undefined ? JSON.stringify(body) : undefined }).then(parseJsonSafe),
};
